// import Material from './material'
import * as StackBlur from 'stackblur-canvas'
// import Swatch from './swatch'
import Stage from 'components/Designer/Stage'
import { Md5 } from 'ts-md5'
import Island from '../Components/Island'

interface QuiltType {
  path: string
  name: string
  scale?: [number, number]
  color?: string
}

class Quilt {
  private _id: number = 0
  private _path: string = ''
  private _name: string
  // NOTE: scale in decimeter given from houdini
  private _scale: [number, number] = [1, 1]
  private _resolution: number = 256
  private _unit: { mm: number; cm: number; dm: number } = {
    dm: 256,
    cm: 25.6,
    mm: 2.56,
  }
  private _rotation: number = 0
  private _islands: Island[] = []
  private _color: string = '#000000'
  private _colorMap: HTMLCanvasElement
  private _bumpMap: HTMLCanvasElement
  private _specMap: HTMLCanvasElement
  private _data: any
  private _debounce_draw: NodeJS.Timeout | undefined
  private _debounce_refresh: NodeJS.Timeout | undefined
  private _uid: string = ''
  private _scaleMultipliers: {
    dm: number
    cm: number
    mm: number
    m: number
  } = {
    m: 10,
    dm: 1,
    cm: 0.1,
    mm: 0.01,
  }

  private get thread() {
    return Stage.threadThickness
  }

  private get threadThickness() {
    // Get the number of pixels that represents the thickness of thread ///////
    return this.unit.mm * Stage.threadThickness.mm
  }

  private get unit() {
    return this._unit
  }

  private set path(s: string) {
    this._path = s
    fetch(this._path)
      .then(e => e.json())
      .then(f => {
        this._data = f
        this.draw({})
      })
      .catch(e => {
        console.error(e)
      })
  }

  private getBaseResolution(): number {
    // stitch_in_mm X stitch_in_px = texture_scale_in_mm X texture_scale_in_px //
    const thrd_mm: number = Stage.threadThickness.mm
    const thrd_px: number = Stage.threadThickness.px

    const tex_mm_x = this._scale[0] / this._scaleMultipliers.mm
    const tex_mm_y = this._scale[0] / this._scaleMultipliers.mm

    const tex_res_x = (tex_mm_x / thrd_mm) * thrd_px
    const tex_res_y = (tex_mm_y / thrd_mm) * thrd_px

    const base_resolution: number = Math.max(tex_res_x, tex_res_y)

    return base_resolution
  }

  private getOptimalResolution(base_res: number = this.getBaseResolution()) {
    // Creating an array of possible resolutions from 2^8 to 2^12 /////////////
    const min = 8
    const max = 12
    const res = []
    for (let i = max; i >= min; i--) {
      res.push(Math.pow(2, i))
    }
    // Get the next best resolution ///////////////////////////////////////////
    let resolution = 0
    for (let r of res) {
      if (base_res <= r) resolution = r
    }
    return resolution
  }

  private setOptimalResolution(): void {
    const base_res = this.getBaseResolution()
    const optimal_res = this.getOptimalResolution(base_res)

    this._resolution = optimal_res

    const dm = optimal_res * ((this._scale[0] + this._scale[1]) / 2)
    this._unit = {
      dm,
      cm: dm / 10,
      mm: dm / 100,
    }
  }

  private generateCanvas(id: string = ''): HTMLCanvasElement {
    const canv = document.createElement('CANVAS') as HTMLCanvasElement
    canv.id = id
    canv.width = canv.height = 64
    document.body.append(canv)
    return canv
  }

  private clear({ bump = true }: { bump?: boolean }): void {
    let canvases
    if (bump) {
      canvases = [this._colorMap, this._bumpMap]
    } else {
      canvases = [this._colorMap]
    }
    for (const canvas of canvases) {
      const n = canvas
      const ntx = n.getContext('2d')
      ntx!.clearRect(0, 0, n.width, n.height)
      // ntx!.beginPath()
    }
  }

  private draw({ bump = true }: { bump?: boolean }): void {
    this.setOptimalResolution()

    const items = this._data
    const res = this._resolution
    const unit = this.unit

    const colorCanvas = this._colorMap
    colorCanvas.width = res
    colorCanvas.height = res

    const bumpCanvas = this._bumpMap
    bumpCanvas.width = res
    bumpCanvas.height = res

    this.clear({ bump })

    const threadThickness = this.threadThickness
    const stitch = {
      length: Stage.stitch.length * this.unit.cm,
      gap: Stage.stitch.gap * this.unit.cm,
    }
    const max = 16
    const crop = 1
    const dip_step = 0.1
    const dips = [
      dip_step, // starting hole
      stitch.length - dip_step * 2, // gap
      dip_step, // ending hole
      stitch.gap, // thread gap
    ]
    const thread = [stitch.length, stitch.gap]

    // Using 1/1024 from Houdini scaling factor
    const scale_factor = (1 / 1024) * res

    // Color Context
    const cntx = colorCanvas.getContext('2d')
    if (cntx) {
      cntx.fillStyle = 'transparent'
      cntx.fillRect(0, 0, res, res)

      cntx.lineJoin = 'round'
      cntx.shadowColor = 'black'
      cntx.lineCap = 'round'

      for (let i = 0; i < items.length; i++) {
        cntx.moveTo(
          items[i][0][0] * scale_factor,
          items[i][0][1] * scale_factor
        )
        for (let ii = 1; ii < items[i].length; ii++) {
          cntx.lineTo(
            items[i][ii][0] * scale_factor,
            items[i][ii][1] * scale_factor
          )
        }
      }

      cntx.strokeStyle = '#000f'
      cntx.shadowColor = 'rgba(0, 0, 0, 1)'
      for (let i = max; i > 0; i--) {
        cntx.lineWidth = threadThickness
        cntx.shadowBlur = Math.pow(2, i / max) * unit.cm * 2 // 2cm tinting
        cntx.shadowOffsetX = (Math.random() * 2 - 1) * unit.mm
        cntx.shadowOffsetY = (Math.random() * 2 - 1) * unit.mm
        cntx.stroke()
      }

      // Brighten canvas
      const crop = 1 // a cropping factor useful only in testing
      var alpha_multiplier = 1 / 2 // alpha multiplier
      const clr_img = cntx.getImageData(0, 0, res / crop, res / crop)
      const clr_img_data = clr_img.data
      for (var i = 0; i < clr_img_data.length; i += 4) {
        clr_img_data[i] = clr_img_data[i + 1] = clr_img_data[i + 2] = 0
        var alpha = clr_img_data[i + 3]
        const new_alpha = Math.round(alpha * alpha_multiplier)
        clr_img_data[i + 3] = Math.max(0, Math.min(255, new_alpha))
      }
      cntx.putImageData(clr_img, 0, 0)

      // Stitch Holes
      cntx.lineWidth = threadThickness * 1.5 + 1 // 1px greater than thread
      cntx.strokeStyle = '#000f'
      cntx.setLineDash(dips)
      cntx.shadowColor = '#000f'
      cntx.shadowBlur = unit.mm * 2
      cntx.shadowOffsetX = cntx.shadowOffsetY = 0
      cntx.stroke()
      cntx.shadowBlur = unit.mm * 1.5
      cntx.stroke()
      cntx.shadowBlur = unit.mm * 1
      cntx.stroke()

      cntx.lineWidth = threadThickness + 0.5
      const thread = [stitch.length, stitch.gap]
      cntx.setLineDash(thread)
      cntx.lineDashOffset = 0
      cntx.strokeStyle = '#000f'
      cntx.shadowColor = '#0000'
      cntx.shadowBlur = 0
      cntx.stroke()

      cntx.lineWidth = threadThickness
      cntx.setLineDash(thread)
      cntx.strokeStyle = this._color
      cntx.stroke()
    }

    // Bump Context
    if (bump) {
      bumpCanvas.width = res
      bumpCanvas.height = res
      const bntx = bumpCanvas.getContext('2d')
      if (bntx) {
        bntx.fillStyle = 'transparent'
        bntx.fillRect(0, 0, res, res)

        bntx.lineJoin = 'round'
        bntx.shadowColor = 'black'
        bntx.lineCap = 'round'

        for (let i = 0; i < items.length; i++) {
          bntx.moveTo(
            items[i][0][0] * scale_factor,
            items[i][0][1] * scale_factor
          )
          for (let ii = 1; ii < items[i].length; ii++) {
            bntx.lineTo(
              items[i][ii][0] * scale_factor,
              items[i][ii][1] * scale_factor
            )
          }
        }

        // BUMP
        let max = 16
        const pwr = Math.sqrt(2)
        bntx.strokeStyle = '#000f'
        bntx.shadowColor = '#000f'
        for (let i = max; i >= 0; i--) {
          // range thread/2 to thread/2 + 1
          const t = threadThickness * 0.5 + i / max
          bntx.lineWidth = t
          bntx.shadowBlur =
            (Math.pow(pwr, i) / Math.pow(pwr, max)) * //
            (unit.cm * 8) // 8cm influence
          // random offset by 0.1mm
          bntx.shadowOffsetX = (Math.random() * 2 - 1) * unit.mm * 0.1
          bntx.shadowOffsetY = (Math.random() * 2 - 1) * unit.mm * 0.1
          const dips2 = [
            stitch.gap + t * 2 + i / max,
            stitch.length - t * 2 - i / max,
          ]
          bntx.setLineDash(dips2)
          bntx.lineDashOffset = stitch.gap - t
          bntx.stroke()

          bntx.setLineDash([])
          bntx.lineWidth = t * 1.2
          bntx.stroke()
        }
        // get current range
        const bmp_img = bntx.getImageData(0, 0, res / crop, res / crop)
        const bmp_img_data = bmp_img.data
        let c_max = 0
        let c_min = 255
        for (var i = 0; i < bmp_img_data.length; i += 4) {
          var alpha = bmp_img_data[i + 3]
          c_max = Math.max(c_max, alpha)
          c_min = Math.min(c_min, alpha)
        }

        // adjust alpha to range with noise
        var range = [0, 40] // inverted range, 0 = white, 255 = black
        for (var i = 0; i < bmp_img_data.length; i += 4) {
          var alpha = bmp_img_data[i + 3]
          const noise_scale = 1
          const noise =
            (Math.random() * noise_scale - noise_scale / 2) * Math.random()
          const new_alpha =
            (alpha - c_min + range[0]) *
              ((c_max - range[1]) / (255 - c_min + range[0])) +
            noise
          const ranged_alpha =
            Math.pow(Math.max(0, Math.min(255, new_alpha)) / 255, 0.666) * 255
          bmp_img_data[i + 3] = Math.round(ranged_alpha)
        }
        bntx.putImageData(bmp_img, 0, 0)

        // NOTE: DEBUG recalculate range
        if (false) {
          c_max = 0
          c_min = 255
          for (var i = 0; i < bmp_img_data.length; i += 4) {
            var alpha = bmp_img_data[i + 3]
            c_max = Math.max(c_max, alpha)
            c_min = Math.min(c_min, alpha)
          }
          console.log('scaled', c_max, c_min)
        }

        // Stitch Holes
        bntx.strokeStyle = '#000f'
        bntx.setLineDash(dips)
        bntx.shadowColor = '#000f'
        bntx.lineWidth = threadThickness
        bntx.shadowBlur = unit.mm * 4
        bntx.shadowOffsetX = bntx.shadowOffsetY = 0
        bntx.stroke()
        bntx.shadowBlur = unit.mm * 2
        bntx.stroke()
        bntx.shadowBlur = 0

        bntx.lineWidth = threadThickness + unit.mm * 0.1
        bntx.shadowColor = '#0000'
        bntx.setLineDash(thread)
        bntx.lineDashOffset = 0
        bntx.strokeStyle = '#0008'
        bntx.stroke()

        const dips2 = [
          stitch.gap - threadThickness * 2,
          stitch.length + threadThickness * 2,
        ]
        bntx.lineDashOffset = stitch.gap - threadThickness
        bntx.lineWidth = threadThickness
        bntx.setLineDash(dips2)
        bntx.strokeStyle = '#000f'
        bntx.shadowBlur = unit.cm * 0.8
        bntx.stroke()

        bntx.shadowBlur = 1
        bntx.lineWidth = threadThickness
        for (let i = 0; i < stitch.length - 0.1; i = i + stitch.length / 12) {
          const j = Math.pow(i / (stitch.length - 0.1), 1.2)
          const k = j * (stitch.length - 1)
          const thread2 = [stitch.length - k, stitch.gap + k]
          bntx.lineDashOffset = -k * 0.5
          bntx.setLineDash(thread2)
          bntx.strokeStyle = `rgba(255,255,255,${j})`
          bntx.stroke()
        }

        bntx.lineWidth = 1
        bntx.shadowBlur = threadThickness
        bntx.setLineDash(thread)
        bntx.strokeStyle = '#fff2'
        bntx.stroke()
      }
    }
    this.refresh()
  }

  // CONSTRUCTOR //////////////////////////////////////////////////////////////
  constructor({ path, name, scale, color }: QuiltType) {
    this._uid = Md5.hashStr(
      `${name}:${path}:${scale ? scale[0] : 1}:${scale ? scale[1] : 1}:${color}`
    )
    if (scale) this._scale = scale
    this._name = name
    // this._islands.push(island)
    this._colorMap = this.generateCanvas(`color:${this._uid}`)
    this._bumpMap = this.generateCanvas(`bump:${this._uid}`)
    this._specMap = this.generateCanvas(`spec:${this._uid}`)
    // if (rotation) this._rotation = rotation
    if (color) this._color = color
    this.path = path
  }

  // get a human readable repr of current uid /////////////////////////////////
  public get idString(): string {
    return (
      this._id +
      ':' +
      this._name +
      ':' +
      this._path +
      ':' +
      this._scale[0].toString() +
      ':' +
      this._scale[1].toString() +
      ':' +
      this._rotation.toString() +
      ':' +
      this._color
    )
  }

  // get a uid repr of current quilt///////////////////////////////////////////
  public get uid(): string {
    if (!this._uid) {
      this._uid = Md5.hashStr(this.idString)
    }
    return this._uid
  }

  public get resolution() {
    return this._resolution
  }

  public get scale() {
    return this._scale
  }

  public get colorMap(): HTMLCanvasElement {
    return this._colorMap
  }

  public get bumpMap(): HTMLCanvasElement {
    return this._bumpMap
  }

  public get specMap(): HTMLCanvasElement {
    return this._specMap
  }

  public get data() {
    return {
      name: this._name,
      path: this._path,
      scale: this._scale,
      rotation: this._rotation,
      stitchColor: this._color,
    }
  }

  public refresh() {
    // Refresh Queue //////////////////////////////////////////////////////////
    if (this._debounce_refresh) clearTimeout(this._debounce_refresh)
    this._debounce_refresh = setTimeout(() => {
      console.log('refresh')
    }, 200)
  }

  public static setQuilt(
    q: {
      name: string
      path: string
      scale: [number, number]
    } | null
  ) {
    if (q) {
      const { name, path, scale } = q
      const quilt = Stage.prepQuilt(new Quilt({ name, path, scale }))
      for (const island of Stage.selection) {
        island.applyQuilt(quilt)
      }
    }
  }

  public static setColor(c: string) {
    for (const island of Stage.selection) {
      if (island.quilt) {
        island.quilt._color = c
        island.quilt.draw({})
        island.refresh()
      }
    }
  }
}

class QuiltElement {
  private _quilt: Quilt | undefined
  private _island: Island
  private _scale: number = 1
  private _rotation: number = 0
  private _enabled: boolean = false
  private _debounce_refresh: NodeJS.Timeout | undefined

  private refresh_(): void {
    // Immediate Refresh //////////////////////////////////////////////////////
    this._island.refresh()
  }

  constructor(island: Island, quilt?: Quilt | undefined) {
    this._quilt = quilt
    this._island = island
  }

  public enable(): void {
    // Enable the current Quilt ///////////////////////////////////////////////
    this._enabled = true
  }

  public disable(): void {
    // Disable the current Quilting ///////////////////////////////////////////
    this._enabled = false
  }

  public get enabled() {
    return this._enabled
  }

  public get quilt(): Quilt | undefined {
    return this.enabled ? this._quilt : undefined
  }

  public set quilt(q: Quilt | undefined) {
    this._quilt = q
    if (q) {
      this.enable()
    } else {
      this.disable()
    }
    this.island.refresh()
  }

  public get island() {
    return this._island
  }

  public set scale(s: number) {
    this._scale = s
  }

  public get scale() {
    return this._scale
  }

  public get rotation() {
    return this._rotation
  }

  public refresh() {
    // Refresh Queue //////////////////////////////////////////////////////////
    if (this._debounce_refresh) clearTimeout(this._debounce_refresh)
    this._debounce_refresh = setTimeout(this.refresh_, 200)
  }
}

export default Quilt
export { QuiltElement }
